import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { brand, fonts } from "../../../themes/theme";
import ViewContainer from "../../../components/ViewContainer";
import DashboardGrid from "../../../components/grid/DashboardGrid";
import Panel from "../../../components/grid/Panel";
import DataTable from "../../../components/DataTable";
import BarList from "../../../components/charts/BarList";
import Heatmap from "../../../components/charts/Heatmap";
import GroupedBarChart from "../../../components/charts/GroupedBarChart";
import MultiSeriesLineChart from "../../../components/charts/MultiSeriesLineChart";
import SubgroupFilterBar, { useSubgroupFilter } from "./SubgroupFilterBar";

import adherenceData from "../../../data/pharma/subgroupsAdherence.json";

const brandHex = { primary: "#3248CA", accent: "#FF5F24" };
const typeTone = { intencional: "accentMedium", noIntencional: "primary" };

// Modulo 3.2.3 — Subgroups · Adherence.
function SubgroupsAdherenceView() {
    const { t } = useTranslation();
    const { filtered, subgroups } = useSubgroupFilter();
    const [programme, setProgramme] = useState("all");

    const rootCauseRows = adherenceData.rootCauseSignal.map((r) => ({
        cause: r.cause,
        share: `${r.sharePct}%`,
        type: (
            <Box sx={{ fontSize: 10.5, fontWeight: 600, color: `${typeTone[r.type]}.main` }}>
                {t(`views.pharmaSubgroupsAdherence.types.${r.type}`)}
            </Box>
        ),
        signal: r.signal,
    }));

    // Igual que el mapa de calor: al filtrar por subgrupo, la barra de
    // persistencia por PSP deja de mostrar la media de toda la cohorte y
    // pasa a mostrar el desglose de ESE subgrupo (mismos datos que el mapa
    // de calor). "Todos los subgrupos" muestra la media de cohorte original
    // (incluye "Educacion sobre la enfermedad", que el mapa de calor no
    // desglosa por subgrupo en la fuente).
    const isSubgroupFiltered = filtered.length !== subgroups.length;
    const heatColumns = adherenceData.heatmapSubgroupByProgramme.columns;
    const pspItems = isSubgroupFiltered
        ? heatColumns.map((col, i) => {
              const rows = adherenceData.heatmapSubgroupByProgramme.rows.filter((r) => filtered.some((s) => s.id === r.subgroupId));
              const avg = Math.round(rows.reduce((sum, r) => sum + r.values[i], 0) / rows.length);
              return { label: col, pct: avg, value: `${avg}%`, color: avg >= 85 ? "primary.main" : undefined };
          })
        : adherenceData.pspPersistence12m.map((p) => ({
              label: p.programme,
              pct: p.pct,
              value: `${p.pct}%`,
              color: p.pct >= 85 ? "primary.main" : undefined,
          }));

    const heatRows = adherenceData.heatmapSubgroupByProgramme.rows
        .filter((r) => filtered.some((s) => s.id === r.subgroupId))
        .map((r) => ({ label: subgroups.find((s) => s.id === r.subgroupId)?.name || r.subgroupId, values: r.values }));

    const resolutionRows = adherenceData.rootCauseResolution.map((r) => ({
        label: `${r.cause} (${r.intervention})`,
        values: [r.before, r.after],
    }));

    // Divergencia engagement/adherencia: curva base reescalada de forma
    // ilustrativa segun el subgrupo/programa elegido (demo, no un modelo real).
    const eng = adherenceData.engagementVsAdherence;
    const columns = adherenceData.heatmapSubgroupByProgramme.columns;
    const progIndex = programme === "all" ? -1 : adherenceData.engagementVsAdherence.filters.programmes.indexOf(programme);
    const heatColIndex = progIndex >= 0 ? columns.findIndex((c) => programme.toLowerCase().includes(c.toLowerCase().split(" ")[0])) : -1;

    const scaleFactor = useMemo(() => {
        const rows = adherenceData.heatmapSubgroupByProgramme.rows;
        const subgroupIds = filtered.map((s) => s.id);
        const relevantRows = rows.filter((r) => subgroupIds.includes(r.subgroupId));
        if (!relevantRows.length) return 1;
        const colIdx = heatColIndex >= 0 ? heatColIndex : null;
        const values = relevantRows.map((r) => (colIdx != null ? r.values[colIdx] : r.values.reduce((a, b) => a + b, 0) / r.values.length));
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        const overallAvg = rows.flatMap((r) => r.values).reduce((a, b) => a + b, 0) / rows.flatMap((r) => r.values).length;
        return avg / overallAvg;
    }, [filtered, heatColIndex]);

    const isFiltered = programme !== "all" || filtered.length !== subgroups.length;
    const adherenceScaled = eng.adherence.map((v, i) => (i === 0 ? v : Math.min(99, Math.round(v * scaleFactor))));
    const chartData = eng.months.map((m, i) => ({ x: m, engagement: eng.engagement[i], adherence: isFiltered ? adherenceScaled[i] : eng.adherence[i] }));
    const chartSeries = [
        { key: "engagement", name: t("views.pharmaSubgroupsAdherence.series.engagement"), color: brandHex.accent },
        { key: "adherence", name: t("views.pharmaSubgroupsAdherence.series.adherence"), color: brandHex.primary },
    ];

    return (
        <ViewContainer title={t("views.pharmaSubgroupsAdherence.title")} description={t("views.pharmaSubgroupsAdherence.description")}>
            <SubgroupFilterBar />
            <DashboardGrid gap={2} rowGap={2.5}>
                <Panel span={12} dense title={t("views.pharmaSubgroupsAdherence.rootCauseTitle")} subtitle={t("views.pharmaSubgroupsAdherence.rootCauseHint")}>
                    <DataTable
                        columns={[
                            { key: "cause", label: t("views.pharmaSubgroupsAdherence.table.cause"), width: "1fr" },
                            { key: "share", label: t("views.pharmaSubgroupsAdherence.table.share"), align: "right", width: "0.5fr" },
                            { key: "type", label: t("views.pharmaSubgroupsAdherence.table.type"), width: "0.7fr" },
                            { key: "signal", label: t("views.pharmaSubgroupsAdherence.table.signal"), width: "2fr" },
                        ]}
                        rows={rootCauseRows}
                    />
                </Panel>

                <Box sx={{ gridColumn: "span 12", mt: 0.5 }}>
                    <Typography sx={{ fontFamily: "inherit", fontSize: 15, color: "text.primary" }}>
                        {t("views.pharmaSubgroupsAdherence.pspSectionTitle")}
                    </Typography>
                </Box>

                <Panel span={{ xs: 12, md: 5 }} dense title={t("views.pharmaSubgroupsAdherence.pspPersistenceTitle")} subtitle={t("views.pharmaSubgroupsAdherence.pspPersistenceHint")}>
                    <BarList items={pspItems} />
                    <Typography sx={{ fontSize: 10.5, color: "text.secondary", mt: 1, fontStyle: "italic" }}>
                        {t(isSubgroupFiltered ? "views.pharmaSubgroupsAdherence.pspPersistenceFilteredNote" : "views.pharmaSubgroupsAdherence.pspPersistenceAllNote")}
                    </Typography>
                </Panel>

                <Panel span={{ xs: 12, md: 7 }} dense title={t("views.pharmaSubgroupsAdherence.heatmapTitle")} subtitle={t("views.pharmaSubgroupsAdherence.heatmapHint")}>
                    <Heatmap rows={heatRows} columns={columns} />
                </Panel>

                <Panel span={{ xs: 12, md: 5 }} dense title={t("views.pharmaSubgroupsAdherence.rootCauseResolutionTitle")} subtitle={t("views.pharmaSubgroupsAdherence.rootCauseResolutionHint")}>
                    <GroupedBarChart
                        rows={resolutionRows}
                        series={[
                            { name: t("views.pharmaSubgroupsAdherence.table.before"), color: brand.gray200 },
                            { name: t("views.pharmaSubgroupsAdherence.table.after"), color: brandHex.primary },
                        ]}
                    />
                </Panel>

                <Panel span={{ xs: 12, md: 7 }} dense title={t("views.pharmaSubgroupsAdherence.engagementTitle")} subtitle={t("views.pharmaSubgroupsAdherence.engagementHint")}>
                    <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                        <Box
                            component="select"
                            aria-label={t("views.pharmaSubgroupsAdherence.filters.programmeAll")}
                            value={programme}
                            onChange={(e) => setProgramme(e.target.value)}
                            sx={{
                                p: "5px 9px",
                                bgcolor: "#fff",
                                border: "1px solid",
                                borderColor: brand.borderLight,
                                borderRadius: "7px",
                                fontSize: 12,
                                color: "text.primary",
                                fontFamily: fonts.body,
                                cursor: "pointer",
                                outline: "none",
                                "&:focus": { borderColor: "primary.main" },
                            }}
                        >
                            <option value="all">{t("views.pharmaSubgroupsAdherence.filters.programmeAll")}</option>
                            {eng.filters.programmes.map((p) => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </Box>
                    </Box>
                    <MultiSeriesLineChart data={chartData} series={chartSeries} yDomain={[0, 100]} height={220} />
                    {isFiltered && (
                        <Typography sx={{ fontSize: 10.5, color: "text.secondary", mt: 1, fontStyle: "italic" }}>
                            {t("views.pharmaSubgroupsAdherence.engagementIllustrativeNote")}
                        </Typography>
                    )}
                </Panel>
            </DashboardGrid>
        </ViewContainer>
    );
}

export default SubgroupsAdherenceView;
